import { Moment } from 'moment';
import upperFirst from 'lodash/upperFirst';
import compact from 'lodash/compact';

import { BasketPrices, Promotion } from '@front/common/src/types/commons';
import { isCountryFR } from '@front/common/src/utils/country';
import { formatPrice, formatPricePerHour } from '@front/common/src/utils/formatting';
import { hasPrestations } from '@front/common/src/utils/basket';
import { BasketState } from '@front/common/src/ducks/common/basket';
import { isRecurrentRecurrence } from '@front/common/src/utils/cleaning';
import { formatPrimaryAppointment } from '@front/common/src/utils/appointment';
import { formatDate, FORMATS } from '@front/common/src/utils/date';
import { AlertCommonProps } from '@front/common/src/components/alert/types';
import { getMessages } from '@front/common/src/utils/errors';

import { translate } from '@front/i18n';

const t = translate('prices');

export type DetermineTotalLineParams = {
  prices: BasketPrices;
  duration: number | null;
  promotion: BasketState['promotion'];
  hasMembership: boolean;
};

// TODO_PRICES: remove when migration done in mobile
export const determineTotalLine = ({ prices, promotion, duration, hasMembership }: DetermineTotalLineParams) => {
  if (prices.kind === 'wellness') {
    return formatPrice(hasMembership ? prices.membership.totalPrice : prices.totalPrice);
  }

  if (prices.kind === 'cleaning') {
    const { totalPriceAfterTax, totalHourlyPriceAfterTax, giftValue, promoValue } = hasMembership
      ? prices.membership
      : prices;

    if (giftValue) {
      return formatPrice(duration ? totalPriceAfterTax : 0);
    }

    if (promoValue) {
      if (duration) {
        return formatPrice(totalPriceAfterTax);
      }

      // No duration
      return promotion.calculation_type === 'per_hour'
        ? formatPricePerHour(totalHourlyPriceAfterTax)
        : formatPrice(0);
    }

    return formatPricePerHour(totalHourlyPriceAfterTax);
  }

  // childcare
  return formatPricePerHour(prices.totalHourlyPriceAfterTax);
};

export type DetermineTaxLineParams = {
  prices: BasketPrices;
  duration: number | null;
  promotion: BasketState['promotion'];
  hasMembership: boolean;
};

// TODO_PRICES: remove when migration done in mobile
export const determineTaxLine = ({ prices, duration, promotion, hasMembership }: DetermineTaxLineParams) => {
  // tax line is undefined if wellness or if country is not FR
  if (prices.kind === 'wellness' || !isCountryFR()) return undefined;

  // for childcare or cleaning
  if (!prices.totalHourlyPriceAfterTax) return undefined;

  if (prices.kind === 'cleaning') {
    const { totalPriceToShow, totalHourlyPrice, giftValue, promoValue } = hasMembership ? prices.membership : prices;

    if (giftValue) {
      return duration
        ? { text: t('.before_tax'), price: formatPrice(totalPriceToShow) }
        : { text: t('.tax_credit_50') };
    }

    if (promoValue) {
      if (duration) {
        return { text: t('.before_tax'), price: formatPrice(totalPriceToShow) };
      }

      // No duration
      return promotion.calculation_type === 'per_hour'
        ? { text: t('.before_tax'), price: formatPricePerHour(totalPriceToShow) }
        : { text: t('.tax_credit_50') };
    }

    return { text: t('.before_tax'), price: formatPricePerHour(totalHourlyPrice) };
  }

  // childcare
  return { text: t('.before_tax'), price: formatPricePerHour(prices.totalHourlyPrice) };
};

type ShouldFormatPromotionPerHourParams = {
  promotion?: Promotion;
  duration?: BasketState['duration'];
};

export const shouldFormatPromotionPerHour = ({ promotion, duration }: ShouldFormatPromotionPerHourParams) =>
  promotion?.calculation_type === 'per_hour' && !duration;

type GetPromotionAlertContent = (params: { promotion?: Promotion; error?: Error | null }) => {
  type: Extract<AlertCommonProps['type'], 'success' | 'error'>;
  content: string | string[];
};

export const getPromotionAlertContent: GetPromotionAlertContent = ({ promotion, error }) => {
  if (promotion?.success) {
    return {
      type: 'success',
      content: t('components.promo_code.success', { code: promotion.code }),
    };
  }

  return {
    type: 'error',
    // Backend validation errors are in promotion.message, error is for unexpected errors
    content: compact([...(error ? getMessages(error) : []), promotion?.message]),
  };
};

type GetAppointmentDescriptionIfNoSecondaryProps = {
  primary: Moment | null;
  appointmentDate?: Moment | null;
  weeklyRecurrence?: number | null;
  isOnCalendarScreen?: boolean;
};

export const getAppointmentDescriptionIfNoSecondary = ({
  primary,
  appointmentDate,
  weeklyRecurrence,
  isOnCalendarScreen = false,
}: GetAppointmentDescriptionIfNoSecondaryProps) => {
  const isRecurrent = isRecurrentRecurrence(weeklyRecurrence);
  const type = isRecurrent ? 'recurrent' : 'spot';
  if (primary) {
    return t(`basket.cleaning.appointment_hour.primary_with_no_secondary.${type}.description`);
  }
  if (!appointmentDate) {
    return t(`basket.cleaning.appointment_calendar.nothing_selected.${type}.description`);
  }
  if (isOnCalendarScreen) {
    return t('basket.cleaning.appointment_calendar.date_selected_only.description');
  }
  if (!isRecurrent) {
    return t('basket.cleaning.appointment_hour.date_selected_only.spot.description');
  }
  if (weeklyRecurrence && weeklyRecurrence > 1) {
    return t('basket.cleaning.appointment_hour.date_selected_only.several.description');
  }
  return t('basket.cleaning.appointment_hour.date_selected_only.weekly.description');
};

type GetAppointmentAccordionTitleProps = {
  primary: Moment | null;
  appointmentDate?: Moment | null;
  weeklyRecurrence?: number | null;
};

export const getAppointmentAccordionTitle = ({
  primary,
  appointmentDate,
  weeklyRecurrence,
}: GetAppointmentAccordionTitleProps) => {
  const type = isRecurrentRecurrence(weeklyRecurrence) ? 'recurrent' : 'spot';

  // After booking creation
  if (primary) {
    return formatPrimaryAppointment(primary, weeklyRecurrence);
  }

  // Before booking creation (inside funnel), no primary yet selected
  if (!appointmentDate) {
    return t(`basket.cleaning.appointment_calendar.nothing_selected.${type}.title`);
  }

  // Before booking creation (inside funnel), primary selected for recurrent cleaning
  if (weeklyRecurrence) {
    const date = formatDate(appointmentDate, FORMATS.DATE.WEEKDAY_DAY_MONTH_SHORT);
    if (weeklyRecurrence === 0.25) {
      return t('appointment.primary.every_4_weeks', {
        first: date,
        day: formatDate(appointmentDate, FORMATS.DATE.WEEKDAY),
      });
    }
    if (weeklyRecurrence === 0.5) {
      return t('appointment.primary.biweekly', {
        first: date,
        day: formatDate(appointmentDate, FORMATS.DATE.WEEKDAY),
      });
    }
    if (weeklyRecurrence === 1) {
      return t('appointment.primary.weekly', {
        first: date,
        day: formatDate(appointmentDate, FORMATS.DATE.WEEKDAY),
      });
    }
    if (weeklyRecurrence > 1) {
      return t('appointment.primary.several', { first: date, recurrence: weeklyRecurrence });
    }
  }

  return upperFirst(formatDate(appointmentDate, FORMATS.DATE.WEEKDAY_DAY_MONTH));
};

export type HasBasketReachedMinimumPriceArgs = {
  basketContent: BasketState['basketContent'];
  currentPrice: number;
  minimumPrice: number;
  minPriceSurchargeAccepted: boolean;
};

export const hasBasketReachedMinimumPrice = ({
  basketContent,
  currentPrice,
  minimumPrice,
  minPriceSurchargeAccepted,
}: HasBasketReachedMinimumPriceArgs) => {
  if (!hasPrestations(basketContent)) return false;
  if (minPriceSurchargeAccepted) return true;

  return currentPrice >= minimumPrice;
};
